/* What the shop says about the garments the site already knows how to draw:
 * the price, the sizes that exist, and which of them can still be bought.
 *
 * Fetched once for the whole catalogue rather than per page, because it is one
 * request for five products and every page that shows a price wants it at the
 * same moment. Nothing here is written back to Shopify — that is the cart's
 * job. */

import { reactive } from 'vue';
import { fetchProducts, isConfigured } from '@/api/shopify.js';
import { PRODUCTS } from '@/config/products.js';

/* Sizes come back in the order the shop happens to hold them — the tanktop
   lists S before XS — and a size picker that is not in size order reads as
   broken. Anything unlisted keeps its place at the end rather than being
   dropped. */
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL'];

function bySize(a, b) {
  const left = SIZE_ORDER.indexOf(a.size.toUpperCase());
  const right = SIZE_ORDER.indexOf(b.size.toUpperCase());
  if (left === -1 && right === -1) return 0;
  if (left === -1) return 1;
  if (right === -1) return -1;
  return left - right;
}

export const catalogue = reactive({
  /* 'unconfigured' is not a failure: a checkout without the credentials still
     has to show the photography and the copy, it just cannot sell. */
  status: isConfigured ? 'idle' : 'unconfigured',
  error: null,
  // By slug, so callers stay in this site's vocabulary.
  entries: {},

  async load() {
    if (this.status === 'loading' || this.status === 'ready' || this.status === 'unconfigured') {
      return;
    }

    this.status = 'loading';
    this.error = null;

    try {
      const handles = PRODUCTS.map((product) => product.shopifyHandle);
      const found = await fetchProducts(handles);

      const entries = {};
      found.forEach((remote) => {
        const local = PRODUCTS.find((product) => product.shopifyHandle === remote.handle);
        if (!local) return;
        entries[local.slug] = {
          availableForSale: remote.availableForSale,
          variants: [...remote.variants].sort(bySize),
        };
      });

      this.entries = entries;
      this.status = 'ready';
    } catch (error) {
      // Kept so a retry is possible; the pages show their own wording rather
      // than Shopify's, which is written for a developer.
      this.error = error.message;
      this.status = 'error';
    }
  },
});

export function getEntry(slug) {
  return catalogue.entries[slug] || null;
}

/* The cheapest variant is what a product page leads with. They are all one
   price today, but a shop that puts one size up a euro should not make the
   page lie about it. */
export function getFromPrice(slug) {
  const entry = getEntry(slug);
  if (!entry || !entry.variants.length) return null;
  return entry.variants.reduce(
    (cheapest, variant) => (Number(variant.price.amount) < Number(cheapest.amount) ? variant.price : cheapest),
    entry.variants[0].price,
  );
}

export function getVariant(slug, size) {
  const entry = getEntry(slug);
  if (!entry) return null;
  return entry.variants.find((variant) => variant.size === size) || null;
}
